<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useTheme } from 'vuetify';
import { useConfigStore } from '@core/stores/config';
import { Layout, Skins, Theme } from '@core/enums';
import { AppContentLayoutNav, ContentWidth } from '@layouts/enums';

definePage({ meta: { requiresAuth: true } });

const configStore = useConfigStore();
const vuetifyTheme = useTheme();

/**
 * Todo esto vive en `configStore`, que la plantilla persiste en localStorage
 * (namespaceado por `themeConfig.app.title`). Es decir: se aplica al instante,
 * no hay que guardar, y es por navegador — no viaja con la cuenta, a diferencia
 * de Preferences.
 *
 * El color primario queda deliberadamente fuera: es identidad de marca, no una
 * preferencia del usuario.
 */
const THEME_MODES = [
    { value: Theme.Light, label: 'Light', icon: 'ri-sun-line', hint: 'Always light' },
    { value: Theme.Dark, label: 'Dark', icon: 'ri-moon-clear-line', hint: 'Always dark' },
    { value: Theme.System, label: 'System', icon: 'ri-computer-line', hint: 'Follows your OS' },
];

const SKINS = [
    { value: Skins.Default, label: 'Default', icon: 'ri-layout-2-line', hint: 'Cards with a soft shadow' },
    { value: Skins.Bordered, label: 'Bordered', icon: 'ri-layout-grid-line', hint: 'Cards outlined, no shadow' },
];

const LAYOUTS = [
    { value: Layout.Vertical, label: 'Vertical', icon: 'ri-layout-left-line', hint: 'Sidebar on the left' },
    { value: Layout.Collapsed, label: 'Collapsed', icon: 'ri-menu-fold-line', hint: 'Icons-only sidebar' },
    { value: Layout.Horizontal, label: 'Horizontal', icon: 'ri-layout-top-line', hint: 'Menu across the top' },
];

const CONTENT_WIDTHS = [
    { value: ContentWidth.Boxed, label: 'Compact', icon: 'ri-align-center', hint: 'Content capped and centred' },
    { value: ContentWidth.Fluid, label: 'Wide', icon: 'ri-align-justify', hint: 'Content fills the screen' },
];

// 'collapsed' no es un layout propio sino vertical + nav colapsado, así que se
// traduce en ambos sentidos (igual que hace el customizer de la plantilla).
const currentLayout = ref<'vertical' | 'collapsed' | 'horizontal'>(
    configStore.isVerticalNavCollapsed ? 'collapsed' : configStore.appContentLayoutNav,
);

watch(currentLayout, value => {
    if (value === 'collapsed') {
        configStore.isVerticalNavCollapsed = true;
        configStore.appContentLayoutNav = AppContentLayoutNav.Vertical;
    } else {
        configStore.isVerticalNavCollapsed = false;
        configStore.appContentLayoutNav = value;
    }
});

watch(
    () => configStore.isVerticalNavCollapsed,
    () => {
        currentLayout.value = configStore.isVerticalNavCollapsed
            ? 'collapsed'
            : configStore.appContentLayoutNav;
    },
);

// El menú semi-oscuro solo existe en layout vertical y tema claro: en oscuro no
// se distingue del fondo y en horizontal no hay sidebar que teñir.
const semiDarkAvailable = computed(
    () => vuetifyTheme.global.name.value === 'light'
        && configStore.appContentLayoutNav === AppContentLayoutNav.Vertical,
);
</script>

<template>
  <div>
    <v-card border flat class="pa-6 mb-6">
      <h2 class="text-h6 font-weight-bold mb-1">Appearance</h2>
      <p class="text-body-2 text-medium-emphasis mb-5">
        Saved in this browser, so each person on the team can pick their own.
      </p>

      <div class="text-body-2 font-weight-medium mb-2">Mode</div>
      <v-item-group v-model="configStore.theme" mandatory class="d-flex flex-wrap ga-3 mb-6">
        <v-item v-for="mode in THEME_MODES" :key="mode.value" v-slot="{ isSelected, toggle }" :value="mode.value">
          <v-card
            border
            flat
            :color="isSelected ? 'primary' : undefined"
            :variant="isSelected ? 'tonal' : 'flat'"
            class="pa-4 cursor-pointer"
            style="min-inline-size: 150px;"
            @click="toggle"
          >
            <v-icon :icon="mode.icon" size="24" class="mb-2" />
            <div class="text-body-1 font-weight-medium">{{ mode.label }}</div>
            <div class="text-caption text-medium-emphasis">{{ mode.hint }}</div>
          </v-card>
        </v-item>
      </v-item-group>

      <div class="text-body-2 font-weight-medium mb-2">Skin</div>
      <v-item-group v-model="configStore.skin" mandatory class="d-flex flex-wrap ga-3 mb-6">
        <v-item v-for="skin in SKINS" :key="skin.value" v-slot="{ isSelected, toggle }" :value="skin.value">
          <v-card
            border
            flat
            :color="isSelected ? 'primary' : undefined"
            :variant="isSelected ? 'tonal' : 'flat'"
            class="pa-4 cursor-pointer"
            style="min-inline-size: 220px;"
            @click="toggle"
          >
            <v-icon :icon="skin.icon" size="24" class="mb-2" />
            <div class="text-body-1 font-weight-medium">{{ skin.label }}</div>
            <div class="text-caption text-medium-emphasis">{{ skin.hint }}</div>
          </v-card>
        </v-item>
      </v-item-group>

      <v-switch
        v-model="configStore.isVerticalNavSemiDark"
        :disabled="!semiDarkAvailable"
        color="primary"
        density="comfortable"
        hide-details
        label="Semi-dark menu"
        :messages="semiDarkAvailable ? undefined : 'Only available with the light theme and a vertical layout.'"
      />
    </v-card>

    <v-card border flat class="pa-6">
      <h2 class="text-h6 font-weight-bold mb-1">Layout</h2>
      <p class="text-body-2 text-medium-emphasis mb-5">
        Where the navigation sits and how wide the content runs.
      </p>

      <div class="text-body-2 font-weight-medium mb-2">Navigation</div>
      <v-item-group v-model="currentLayout" mandatory class="d-flex flex-wrap ga-3 mb-6">
        <v-item v-for="layout in LAYOUTS" :key="layout.value" v-slot="{ isSelected, toggle }" :value="layout.value">
          <v-card
            border
            flat
            :color="isSelected ? 'primary' : undefined"
            :variant="isSelected ? 'tonal' : 'flat'"
            class="pa-4 cursor-pointer"
            style="min-inline-size: 170px;"
            @click="toggle"
          >
            <v-icon :icon="layout.icon" size="24" class="mb-2" />
            <div class="text-body-1 font-weight-medium">{{ layout.label }}</div>
            <div class="text-caption text-medium-emphasis">{{ layout.hint }}</div>
          </v-card>
        </v-item>
      </v-item-group>

      <div class="text-body-2 font-weight-medium mb-2">Content width</div>
      <v-item-group v-model="configStore.appContentWidth" mandatory class="d-flex flex-wrap ga-3">
        <v-item v-for="width in CONTENT_WIDTHS" :key="width.value" v-slot="{ isSelected, toggle }" :value="width.value">
          <v-card
            border
            flat
            :color="isSelected ? 'primary' : undefined"
            :variant="isSelected ? 'tonal' : 'flat'"
            class="pa-4 cursor-pointer"
            style="min-inline-size: 220px;"
            @click="toggle"
          >
            <v-icon :icon="width.icon" size="24" class="mb-2" />
            <div class="text-body-1 font-weight-medium">{{ width.label }}</div>
            <div class="text-caption text-medium-emphasis">{{ width.hint }}</div>
          </v-card>
        </v-item>
      </v-item-group>
    </v-card>
  </div>
</template>
